/* @HEADER
 * @version 1.1.668 | 2026-10-22
 * @purpose Pure decision logic for whether an installed service-worker state means "an update is waiting to be applied", the visible notice's idle/working/failure/stale/dismissed state machine, and the storage-backed repeat-attempt guard (ZVX-DEV-4423) that stops a failed update from re-prompting in a tight loop — framework-free so it is directly testable with node:test.
 * @sidecar swUpdate.ts.header.md
 * @layer app | @hex _none_ | @ctx _none_
 * @public true
 * @edit careful
 */

// SpecRefs: ZVX-DEV-4423, ZVX-DEV-6219, ZVX-DEV-6227

/**
 * The state values a ServiceWorker can report via its `statechange` event.
 * Mirrors the DOM `ServiceWorkerState` union without importing lib.dom types,
 * so this module stays trivially importable from a plain node:test file.
 */
export type ServiceWorkerStateLike =
  | 'installing'
  | 'installed'
  | 'activating'
  | 'activated'
  | 'redundant'
  | string;

export interface ShouldPromptForUpdateInput {
  /** The installing worker's current `state`. */
  state: ServiceWorkerStateLike;
  /** Whether `navigator.serviceWorker.controller` is already set — true only
   *  when a PREVIOUS worker already controls this page, i.e. this install is
   *  an update rather than the very first SW installation for this origin. */
  hasController: boolean;
}

/**
 * Decides whether a newly-installed worker represents an update the user
 * should be prompted about.
 *
 * `installed` + no controller means this is the FIRST install for this
 * origin (nothing was running before) — there is nothing to "update" from,
 * so no prompt. `installed` + a controller means a previous worker was
 * already active and is now being replaced — that is exactly the moment a
 * waiting worker exists and the person should be told a new build shipped.
 * Every other state (`installing`, `activating`, `activated`, `redundant`)
 * is transient or terminal and is never itself the prompt trigger.
 */
export function shouldPromptForUpdate({
  state,
  hasController,
}: ShouldPromptForUpdateInput): boolean {
  return state === 'installed' && hasController === true;
}

/**
 * The `window` CustomEvent name registerSW.ts dispatches once
 * {@link shouldPromptForUpdate} returns true. A dedicated constant (rather
 * than a string literal repeated in registerSW.ts and the notice component)
 * keeps the two sides of this seam from drifting independently.
 */
export const SW_UPDATE_AVAILABLE_EVENT = 'zvenix:sw-update-available';

/** Detail payload carried by {@link SW_UPDATE_AVAILABLE_EVENT} and {@link SW_UPDATE_STALE_EVENT}. */
export interface SwUpdateAvailableDetail {
  registration: ServiceWorkerRegistration;
  /**
   * ZVX-DEV-4423 — the version the heartbeat path detected the server
   * offering, when known. Absent for the SW-install path (which has no
   * comparable version string of its own) and for any older caller. The
   * notice records this alongside the click so the NEXT boot can tell
   * "we already tried this one" apart from "this is new".
   */
  serverVersion?: string;
  /**
   * ZVX-DEV-6219 — true when the heartbeat learned of this version through
   * the api host's copy of the marker because the web origin's own answered
   * 5xx or not at all. An apply that then fails is then told apart from an
   * ordinary failure: the server is updated, this network cannot see it yet.
   */
  viaFallback?: boolean;
}

/**
 * ZVX-DEV-3684 — self-resolution. The `window` CustomEvent name
 * `registerSW.ts` dispatches when a later heartbeat poll finds the server
 * version no longer newer than this build (`isNewerVersionAvailable()` flips
 * back to `false` after having been `true`) — i.e. the update this page was
 * telling the person about has already landed some other way (typically:
 * the background revalidation from an earlier poll finished, and a later
 * navigation somewhere picked up the fresh cache). Reported live by the
 * operator: his tab eventually showed the new build on its own, more than
 * ten minutes after clicking, with the banner still sitting there having
 * done nothing further. A banner offering to fix something already fixed is
 * its own kind of "click and nothing happens" — this event is what lets
 * `UpdateAvailableNotice` drop itself instead of sitting there stale.
 */
export const SW_UPDATE_RESOLVED_EVENT = 'zvenix:sw-update-resolved';

/**
 * ZVX-DEV-4423 — dispatched instead of {@link SW_UPDATE_AVAILABLE_EVENT}
 * when {@link isRepeatedFailedAttempt} says this exact update was already
 * tried from this exact version. Fired at most once per stale attempt (the
 * heartbeat path gates re-dispatch the same way it already gates
 * `SW_UPDATE_AVAILABLE_EVENT`) — the person is told once that the update did
 * not take, with a manual retry, rather than being re-asked on every poll.
 */
export const SW_UPDATE_STALE_EVENT = 'zvenix:sw-update-stale';

/**
 * ZVX-DEV-6227 — the quiet path. Dispatched by `registerSW.ts` when a tab
 * that was hidden for the idle threshold comes back with a newer build
 * already known and nothing being edited: the notice runs its apply path
 * with no click and renders nothing while it does. Same detail shape as
 * {@link SW_UPDATE_AVAILABLE_EVENT}. If the apply then fails, the ordinary
 * dismissible prompt takes over — the quiet path never leaves silence.
 */
export const SW_UPDATE_AUTO_APPLY_EVENT = 'zvenix:sw-update-auto-apply';

/**
 * ZVX-DEV-3680 — the requested-reload guard.
 *
 * REGRESSION this exists to close: `sw.js` keeps `clients.claim()` in
 * `activate` (deliberate — see sw.js's header). On a person's very FIRST
 * visit, `clients.claim()` takes control of the already-loaded page, which
 * fires `navigator.serviceWorker.controllerchange` — the SAME event that
 * fires when a waiting worker activates after the person clicks "Update".
 * `UpdateAvailableNotice.tsx` could not tell those two apart and reloaded on
 * EVERY `controllerchange`, which aborted whatever the page had in flight
 * (measured on staging: the login POST and a worker-script GET both killed
 * mid-request on a first visit that had nothing to update).
 *
 * A `controllerchange` nobody asked for must never reload the page. This
 * module-level flag is the single, narrow answer to "did the person just
 * click the update button": {@link markUpdateRequested} is called at the
 * moment `SKIP_WAITING` is posted (the ONLY place a reload may originate),
 * and {@link consumeUpdateRequested} is what the `controllerchange`
 * listener checks — consuming (not just reading) the flag so a second,
 * unrelated `controllerchange` later in the session cannot ride the same
 * "yes" answer.
 */
let updateRequestedByUser = false;

/** Call exactly once, at the moment this page posts `{type:'SKIP_WAITING'}` to a waiting worker — never anywhere else. */
export function markUpdateRequested(): void {
  updateRequestedByUser = true;
}

/**
 * Reads AND resets the flag in one step. A `controllerchange` handler must
 * reload only when this returns `true`, and must call it at most once per
 * `controllerchange` event so the flag cannot leak into a later, unrelated
 * `controllerchange`.
 */
export function consumeUpdateRequested(): boolean {
  const was = updateRequestedByUser;
  updateRequestedByUser = false;
  return was;
}

/** Test-only reset — mirrors the `_reset()` convention the template starter app used (removed in ZVX-DEV-4496) so module-level state cannot bleed between node:test cases. */
export function _resetUpdateRequestedForTests(): void {
  updateRequestedByUser = false;
}

/**
 * ZVX-DEV-7995 — a plain reload, as a seam. The update prompt uses it when the server has refused this build and
 * there is no service worker to ask for a new one (the button must still do something); a test cannot reload a
 * jsdom window, so it replaces this instead.
 */
export function reloadPage(): void {
  window.location.reload();
}

/**
 * ZVX-DEV-4423 — the repeat-prompt guard.
 *
 * PROBLEM this closes: `registerSW.ts`'s heartbeat fires an immediate poll
 * on every page load (`armHeartbeatPolling`, ZVX-DEV-3717). A reload that
 * lands back on the SAME build the person just tried to leave — an
 * intermediate cache, a CDN, or any other reason the update did not take —
 * gets that immediate poll re-detecting "newer available" and re-raising
 * the SAME prompt with no new information, indistinguishable from the
 * button having done nothing. Measured live as "press it many times for a
 * single version".
 *
 * FIX: a click records what it was TRYING to reach — `toVersion` — and what
 * build it was leaving — `fromVersion` — in storage that survives the
 * reload a plain module-level flag cannot. The very next boot compares
 * itself against that record: if this page is still on `fromVersion` and
 * the server is still offering exactly `toVersion`, the attempt is a known
 * repeat, not new information — the prompt must say so ONCE rather than
 * re-ask. A DIFFERENT server version (a newer deploy landed meanwhile) is
 * new information regardless of any prior attempt, and is never suppressed.
 */
export const UPDATE_ATTEMPT_STORAGE_KEY = 'zvenix_pwa_update_attempt_v1';

/** The minimal `Storage` surface used here — mirrors `shellSnapshot.ts`'s `StorageLike` so a test can pass a plain object instead of a real `localStorage`. */
export interface UpdateAttemptStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface UpdateAttempt {
  /** The build version this page was running when the click was made. */
  fromVersion: string;
  /** The build version the server was offering at the moment of the click. */
  toVersion: string;
  /** `Date.now()` at the moment of the click. */
  at: number;
}

/** Record an update attempt just before the reload it causes. Never throws — a full or unavailable storage (private mode, quota) must not block the update the person actually asked for. */
export function recordUpdateAttempt(
  storage: UpdateAttemptStorageLike | null | undefined,
  attempt: UpdateAttempt,
): void {
  if (!storage) return;
  try {
    storage.setItem(UPDATE_ATTEMPT_STORAGE_KEY, JSON.stringify(attempt));
  } catch {
    // best-effort only — see doc above
  }
}

/** Read a previously recorded attempt. Returns `null` for anything missing, unreadable, or malformed — never throws. */
export function readUpdateAttempt(
  storage: UpdateAttemptStorageLike | null | undefined,
): UpdateAttempt | null {
  if (!storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(UPDATE_ATTEMPT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<UpdateAttempt>;
    if (typeof parsed?.fromVersion !== 'string' || parsed.fromVersion.length === 0) return null;
    if (typeof parsed?.toVersion !== 'string' || parsed.toVersion.length === 0) return null;
    if (typeof parsed?.at !== 'number' || !Number.isFinite(parsed.at)) return null;
    return { fromVersion: parsed.fromVersion, toVersion: parsed.toVersion, at: parsed.at };
  } catch {
    return null;
  }
}

/** Drop a recorded attempt — called once it is no longer relevant (the versions matched, or the server is now offering something the attempt never targeted). Never throws. */
export function clearUpdateAttempt(storage: UpdateAttemptStorageLike | null | undefined): void {
  if (!storage) return;
  try {
    storage.removeItem(UPDATE_ATTEMPT_STORAGE_KEY);
  } catch {
    // best-effort only
  }
}

export interface IsRepeatedFailedAttemptInput {
  attempt: UpdateAttempt | null;
  /** This build's own version, right now. */
  currentVersion: string | null | undefined;
  /** The version the server is offering, right now. */
  serverVersion: string | null | undefined;
}

/**
 * True only when a PRIOR click already tried to reach exactly this
 * `serverVersion`, starting from exactly this `currentVersion` — i.e.
 * nothing has changed since that attempt: the person is not running a new
 * build, and the server is not offering a newer one either. That, and only
 * that, is "we already tried this exact update and nothing changed" — a
 * genuinely NEW server version (a deploy that landed since the attempt) is
 * new information and is never suppressed by this check, regardless of what
 * `attempt` holds.
 */
export function isRepeatedFailedAttempt({
  attempt,
  currentVersion,
  serverVersion,
}: IsRepeatedFailedAttemptInput): boolean {
  if (!attempt) return false;
  if (typeof currentVersion !== 'string' || currentVersion.length === 0) return false;
  if (typeof serverVersion !== 'string' || serverVersion.length === 0) return false;
  return attempt.fromVersion === currentVersion && attempt.toVersion === serverVersion;
}

/**
 * The minimal `ServiceWorker` surface {@link waitForWaitingWorker} needs —
 * a real `ServiceWorker` satisfies this structurally, and so does a plain
 * test double, with no DOM lib types required in the test file.
 */
export interface MinimalServiceWorker {
  state: ServiceWorkerStateLike;
  addEventListener(type: 'statechange', listener: () => void): void;
  removeEventListener(type: 'statechange', listener: () => void): void;
}

/** The minimal `ServiceWorkerRegistration` surface {@link waitForWaitingWorker} needs. */
export interface MinimalServiceWorkerRegistration {
  waiting: MinimalServiceWorker | null;
  installing: MinimalServiceWorker | null;
  addEventListener(type: 'updatefound', listener: () => void): void;
  removeEventListener(type: 'updatefound', listener: () => void): void;
}

/**
 * ZVX-DEV-3684 — "click Обновить and nothing happens", root cause and fix.
 *
 * The heartbeat path (`registerSW.ts` polling `/health`) can raise the
 * prompt with NO waiting worker yet — the SW-install path alone never got
 * there. Clicking "Update" used to fall straight to a plain
 * `window.location.reload()` in that case, which — now that the app-shell
 * is cache-first (ZVX-DEV-3675) — is answered from the SAME stale cache
 * entry: the reload is instant, the build constant never moves, and the
 * heartbeat immediately re-raises the same prompt. The button looked dead.
 *
 * The fix is to make the click itself CAUSE an update to exist, rather than
 * assume the background revalidation already produced one:
 * `registration.update()` asks the browser to re-check `sw.js` for a real
 * new script, and this function waits — bounded, so a click can never hang
 * the button forever — for that check to either produce a `waiting` worker
 * (a real update WAS available; the caller can `SKIP_WAITING` it and let
 * the existing `controllerchange` reload path finish the job) or time out
 * (no new worker; the caller falls back to a cache-bypassing reload — see
 * `UpdateAvailableNotice.tsx`'s `requestCacheBypassOnce`).
 *
 * Returns immediately if a waiting worker already exists — the SW-install
 * path may have gotten there first, in which case there is nothing to wait
 * for at all.
 */
export function waitForWaitingWorker<R extends MinimalServiceWorkerRegistration>(
  registration: R,
  timeoutMs: number,
): Promise<R['waiting']> {
  if (registration.waiting) return Promise.resolve(registration.waiting);

  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout>;

    function finish(worker: R['waiting']) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      registration.removeEventListener('updatefound', onUpdateFound);
      resolve(worker);
    }

    function onUpdateFound() {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', function onStateChange() {
        if (installing.state === 'installed' && registration.waiting) {
          installing.removeEventListener('statechange', onStateChange);
          finish(registration.waiting);
        }
      });
    }

    registration.addEventListener('updatefound', onUpdateFound);
    timer = setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * ZVX-DEV-3684 — the notice's visible state machine, pulled out as a pure
 * reducer so the "never leave the button looking clickable when it would be
 * a no-op" rule is directly testable, independent of the real SW/network/
 * timer plumbing that drives it in `UpdateAvailableNotice.tsx`.
 *
 * States:
 *   - `idle`      — the default prompt: enabled "Update" button.
 *   - `working`   — a click is in flight: button disabled, "Updating…" label.
 *     Covers BOTH ways an apply attempt resolves successfully (a real
 *     waiting worker was found and SKIP_WAITING was sent, or no worker
 *     appeared in time and the cache-bypassing reload path was taken
 *     instead) — either way the next thing that happens is a real page
 *     navigation, which is its own confirmation. There is deliberately no
 *     separate "success" state to render; see `UpdateAvailableNotice.tsx`.
 *   - `failure`   — the apply attempt could not even be started (e.g.
 *     `registration.update()` rejected, offline). Button becomes "Retry".
 *   - `stale`     — ZVX-DEV-4423: a fresh poll re-detected an update, but
 *     {@link isRepeatedFailedAttempt} says this exact update was already
 *     tried from this exact version with no effect. Button becomes "Retry",
 *     same as `failure`, but the message told to the person is different —
 *     "this did not work" rather than "could not even try" — because unlike
 *     `failure` the apply attempt DID run and DID reload; it just did not
 *     change anything.
 *   - `dismissed` — the notice should render nothing: either the person
 *     closed it, or a later heartbeat poll found the versions now match
 *     (`VERSION_MATCHED`) and there is nothing left to offer.
 */
export type UpdateNoticeState = 'idle' | 'working' | 'failure' | 'stale' | 'dismissed';

export type UpdateNoticeEvent =
  /** A fresh update-available signal arrived (SW-install or heartbeat path) — always resets to `idle`, even from `failure`/`stale`/`dismissed`, since it is a new offer. */
  | { type: 'PROMPT_RAISED' }
  /** ZVX-DEV-4423 — a poll re-detected an update that a prior click already tried and failed to apply. Distinct from `PROMPT_RAISED` so the visible message can say "already tried" instead of the ordinary idle prompt. */
  | { type: 'UPDATE_STALE' }
  /** The person clicked "Update" from the idle prompt. */
  | { type: 'CLICK' }
  /** The person clicked "Retry" from the failure or stale state. */
  | { type: 'RETRY' }
  /** The apply attempt will succeed via a real page navigation (waiting worker found, or the cache-bypass reload path was taken) — stays `working` until the page actually reloads. */
  | { type: 'WORKER_FOUND' }
  /** The apply attempt could not even be started. */
  | { type: 'APPLY_FAILED' }
  /** A later heartbeat poll found the versions now match — nothing left to update to. */
  | { type: 'VERSION_MATCHED' }
  /** The person closed the notice manually. */
  | { type: 'DISMISS' };

export function reduceUpdateNoticeState(
  state: UpdateNoticeState,
  event: UpdateNoticeEvent,
): UpdateNoticeState {
  switch (event.type) {
    case 'PROMPT_RAISED':
      return 'idle';
    case 'UPDATE_STALE':
      return 'stale';
    case 'CLICK':
      return state === 'idle' ? 'working' : state;
    case 'RETRY':
      return state === 'failure' || state === 'stale' ? 'working' : state;
    case 'WORKER_FOUND':
      return state === 'working' ? 'working' : state;
    case 'APPLY_FAILED':
      return state === 'working' ? 'failure' : state;
    case 'VERSION_MATCHED':
      return 'dismissed';
    case 'DISMISS':
      return 'dismissed';
    default:
      return state;
  }
}
